import * as React from 'react';
import {
  Stack,
  SearchBox,
  Text,
  PrimaryButton,
  IconButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';

import { HeroBanner } from '../../../Components/PBT/HeroBanner/HeroBanner';
import { crmPageStyles, alertBannerStyles, searchBoxStyles, actionsBarStyles } from './CRMCasesPage.styled';
import { CRMCaseCard } from './CRMCaseCard';
import { useCRMCases } from './useCRMCases';
import { usePermission } from '../../../Hooks/useFeature';
import { getPaginationItems } from '../../../Utilities/pagination';

const DetailsEntryPanel = React.lazy(() =>
  import('../../../Components/PBT/DetailsEntryPanel/DetailsEntryPanel').then((module) => ({
    default: module.DetailsEntryPanel,
  }))
);
const BulkEntryPanel = React.lazy(() =>
  import('../../../Components/PBT/BulkEntryPanel/BulkEntryPanel').then((module) => ({
    default: module.BulkEntryPanel,
  }))
);

const CRMCasesPage = (): React.ReactElement => {
  const {
    crmState, filteredCases, searchText, setSearchText,
    selectedCase, isDetailsOpen, isBulkOpen, bulkCases, isBulkLoading,
    bulkErrorMessage, setBulkErrorMessage, successMessage, setSuccessMessage,
    handleEnterDetails, handleDetailsSuccess, handleBulkSuccess,
    getCaseFlags, handleSearch, handleRefresh, handlePageChange, handleOpenBulk,
    handleDismissDetails, handleDismissBulk,
  } = useCRMCases();

  // Actions → gate by API permissions (matches the server), not the tree.
  const canBulkCreate = usePermission('Cases.Create');
  const canRefresh = usePermission('CRM.ViewIncoming');

  const heroActions = [...(canRefresh ? [{ text: 'Refresh', icon: '🔄', onClick: handleRefresh }] : [])];
  const hasLoadedCases = crmState.cases.length > 0;
  const isInitialLoading = crmState.isLoading && !hasLoadedCases;

  return (
    <Stack className={crmPageStyles.container}>
      <HeroBanner
        title="CRM Incoming Cases"
        subtitle={`${crmState.totalCount} case(s) pending PBT intake`}
        variant="blue"
        actions={heroActions}
      />

      {crmState.gerWitCount > 0 && (
        <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline={false} styles={alertBannerStyles}>
          <strong>GER/WIT Alert:</strong> {crmState.gerWitCount} incoming case(s) have GER/WIT flags requiring
          compliance review before PBT intake.
        </MessageBar>
      )}
      {crmState.loaCount > 0 && (
        <MessageBar messageBarType={MessageBarType.warning} isMultiline={false} styles={alertBannerStyles}>
          <strong>LOA Alert:</strong> {crmState.loaCount} incoming case(s) involve employees on Leave of Absence.
        </MessageBar>
      )}
      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() => setSuccessMessage(null)}
          styles={alertBannerStyles}
        >
          {successMessage}
        </MessageBar>
      )}
      {bulkErrorMessage && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setBulkErrorMessage(null)} styles={alertBannerStyles}>
          {bulkErrorMessage}
        </MessageBar>
      )}

      <Stack
        horizontal
        verticalAlign="center"
        horizontalAlign="space-between"
        tokens={{ childrenGap: 12 }}
        styles={actionsBarStyles}
      >
        <Stack>
          <SearchBox
            ariaLabel="Search CRM incoming cases by PERN, CRM case, or manager"
            placeholder="Search by PERN, CRM case, or manager..."
            value={searchText}
            onChange={(_, val) => setSearchText(val || '')}
            onSearch={handleSearch}
            onClear={() => handleSearch('')}
            styles={searchBoxStyles}
          />
          <Text className={crmPageStyles.searchHint}>Type 2+ characters or press Enter to search</Text>
        </Stack>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
          {canBulkCreate && (
            <PrimaryButton
              text={isBulkLoading ? 'Loading candidates...' : 'Bulk Entry'}
              onClick={handleOpenBulk}
              disabled={isBulkLoading}
              iconProps={{ iconName: 'BulkUpload' }}
              styles={{ root: { borderRadius: 6 } }}
            />
          )}
          <Text className={crmPageStyles.caseCount}>
            {crmState.searchString ? `${crmState.totalCount} result(s)` : '\u00a0'}
          </Text>
        </Stack>
      </Stack>

      {isInitialLoading && (
        <Stack className={crmPageStyles.spinnerContainer}>
          <Spinner size={SpinnerSize.large} label="Loading CRM cases..." />
        </Stack>
      )}

      {crmState.hasError && !crmState.isLoading && (
        <MessageBar messageBarType={MessageBarType.error}>
          {crmState.errorMessage || 'Failed to load CRM cases.'}
        </MessageBar>
      )}

      {!crmState.isLoading && !crmState.hasError && filteredCases.length === 0 && (
        <Stack className={crmPageStyles.emptyState} horizontalAlign="center" verticalAlign="center">
          <Text className={crmPageStyles.emptyIcon}>📋</Text>
          <Text className={crmPageStyles.emptyTitle}>No CRM cases pending intake</Text>
          <Text className={crmPageStyles.emptySubtitle}>
            {crmState.searchString ? 'No cases match your search.' : 'All CRM cases have been processed.'}
          </Text>
        </Stack>
      )}

      {filteredCases.length > 0 && (
        <>
          <div className={crmPageStyles.caseGrid}>
            {filteredCases.map((c) => (
              <CRMCaseCard key={c.crmCaseId} crmCase={c} flags={getCaseFlags(c)} onEnterDetails={handleEnterDetails} />
            ))}
          </div>
          {crmState.totalPages > 1 && (
            <Stack horizontalAlign="center" tokens={{ childrenGap: 4 }} className={crmPageStyles.paginationBar}>
              <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <IconButton
                  iconProps={{ iconName: 'DoubleChevronLeft' }}
                  title="First page"
                  ariaLabel="first page"
                  disabled={crmState.pageNumber <= 1 || crmState.isLoading}
                  onClick={() => handlePageChange(1)}
                  styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 12 } }}
                />
                <IconButton
                  iconProps={{ iconName: 'ChevronLeft' }}
                  title="Previous page"
                  ariaLabel="previous page"
                  disabled={crmState.pageNumber <= 1 || crmState.isLoading}
                  onClick={() => handlePageChange(crmState.pageNumber - 1)}
                  styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 12 } }}
                />
                {getPaginationItems(crmState.pageNumber - 1, crmState.totalPages).map((item, index) =>
                  item === 'ellipsis' ? (
                    <span key={`ellipsis-${index}`} aria-hidden="true" className={crmPageStyles.paginationEllipsis}>
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handlePageChange(item + 1)}
                      disabled={crmState.isLoading}
                      aria-label={`page ${item + 1}${item + 1 === crmState.pageNumber ? ' selected' : ''}`}
                      aria-current={item + 1 === crmState.pageNumber ? 'page' : undefined}
                      className={
                        item + 1 === crmState.pageNumber
                          ? crmPageStyles.selectedPageButton
                          : crmPageStyles.pageButton
                      }
                    >
                      {item + 1}
                    </button>
                  )
                )}
                <IconButton
                  iconProps={{ iconName: 'ChevronRight' }}
                  title="Next page"
                  ariaLabel="next page"
                  disabled={crmState.pageNumber >= crmState.totalPages || crmState.isLoading}
                  onClick={() => handlePageChange(crmState.pageNumber + 1)}
                  styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 12 } }}
                />
                <IconButton
                  iconProps={{ iconName: 'DoubleChevronRight' }}
                  title="Last page"
                  ariaLabel="last page"
                  disabled={crmState.pageNumber >= crmState.totalPages || crmState.isLoading}
                  onClick={() => handlePageChange(crmState.totalPages)}
                  styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 12 } }}
                />
              </Stack>
              <Text className={crmPageStyles.paginationCount}>
                {(crmState.pageNumber - 1) * crmState.pageSize + 1} -{' '}
                {Math.min(crmState.pageNumber * crmState.pageSize, crmState.totalCount)} of {crmState.totalCount}
              </Text>
            </Stack>
          )}
        </>
      )}
      {isDetailsOpen && (
        <React.Suspense fallback={null}>
          <DetailsEntryPanel
            isOpen
            selectedCase={selectedCase}
            onDismiss={handleDismissDetails}
            onSuccess={handleDetailsSuccess}
          />
        </React.Suspense>
      )}
      {isBulkOpen && (
        <React.Suspense fallback={null}>
          <BulkEntryPanel isOpen cases={bulkCases} onDismiss={handleDismissBulk} onSuccess={handleBulkSuccess} />
        </React.Suspense>
      )}
    </Stack>
  );
};

export default CRMCasesPage;
